import { Label } from '../ui/label'
import { cn } from '../../lib/utils'
import React from 'react'
import InputError from './input-error'

/**
 * InputWrapper component props
 */
export type InputGroupProps = React.PropsWithChildren & {
    /**
     * Wrapper class name,
     * default is `flex flex-col gap-1.5 w-full` 
     */
    wrapperClassName?: string,

    /**
     * Label class name,
     */
    labelClassName?: string,

    /**
     * Label's htmlFor attribute value
     */
    labelFor?: string,

    /**
     * Label For Input Field
     */
    label?: string,

    /**
     * Error message
     */
    error?: string,

    /**
     * Error class name,
     * default is `text-sm text-destructive`
     */
    errorClassName?: string,

    /**
     * Whether the field is dirty (has been modified)
     */
    isDirty?: boolean,

    /**
     * Inline style for label (used so workscope colors always apply)
     */
    labelStyle?: React.CSSProperties,

    /**
     * When true, show yellow background when value is empty (e.g. required fields on Profile)
     */
    highlightWhenEmpty?: boolean,

    /**
     * Optional action(s) to render on the same line as the label (e.g. filter/search icon button)
     */
    labelAction?: React.ReactNode,
}


/**
 * Input group have label and error, 
 * created for wrapping input components, 
 * which need label and error message.
 */
function InputGroup({
    label,
    error,
    wrapperClassName,
    labelClassName,
    labelStyle,
    children,
    errorClassName,
    labelFor,
    isDirty,
    labelAction,
}: InputGroupProps) {
    return (
        <div className={cn("flex flex-col gap-1 w-full", wrapperClassName)}>
            {(label || labelAction) && (
                <div className="flex items-center gap-2 flex-wrap">
                    {label && (
                        <Label 
                            className={cn(
                                "text-sm pl-1 transition-colors duration-200",
                                isDirty && "text-[#9c27b0]",
                                labelClassName
                            )} 
                            htmlFor={labelFor}
                            style={labelStyle}
                        >
                            {label}
                        </Label>
                    )}
                    {labelAction}
                </div>
            )}
            {children}
            {error && <InputError className={errorClassName}>{error}</InputError>}
        </div>
    )
}

export default InputGroup